import React, { useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate, useParams } from 'react-router-dom';
import { useSidebar } from '../../../../context/SidebarContext';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import TotalOrgIcon from '../../../../assets/organization/total-organization-icon.svg';

import {
  PageWrapper,
  HeaderCard,
  IconWrapper,
  HeaderInfo,
  StatusBadge,
  SectionCard,
  SectionInfo,
  ReviewLink,
  VerificationActionCard,
  ActionButtons,
  RejectButton,
  ApproveButton,
  HeaderActions,
  SuspendButton,
  ReactivateButton
} from './styles';
import apiCall from '../../../../utils/api';
import { CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button as MuiButton } from '@mui/material';
import AlertPopup from '../../../../components/common/AlertPopup';

const OrganizationOverviewPage: React.FC = () => {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  const [org, setOrg] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [processing, setProcessing] = React.useState(false);
  const [openRejectDialog, setOpenRejectDialog] = React.useState(false);
  const [rejectReason, setRejectReason] = React.useState('');
  const [toast, setToast] = React.useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetchOrgDetails = useCallback(async () => {
    try {
      const response = await apiCall<any>('get', `/api/organization/superadmin/detail/${id}/`);
      if (response.data && response.data.data) {
        setOrg(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching org details:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrgDetails();
  }, [fetchOrgDetails]);

  useEffect(() => {
    setActivePage('organizations');
    if (org) {
      setPath([
        { title: 'Super Admin', path: '/super-admin' },
        { title: 'Organizations', path: '/super-admin/organizations' },
        { title: org.org_name, path: `/super-admin/organizations/${id}` },
      ]);
    }
    setIsShow(true);
  }, [setActivePage, setPath, setIsShow, id, org]);

  const handleUpdateStatus = async (action: string, reason: string = '') => {
    try {
      setProcessing(true);
      const response = await apiCall<any>('put', `/api/organization/status/${id}/`, {
        action,
        reason
      });
      if (response.status === 200) {
        setToast({ open: true, message: `Organization ${action} successfully`, severity: 'success' });
        fetchOrgDetails();
        setOpenRejectDialog(false);
      }
    } catch (error: any) {
      console.error('Error updating org status:', error);
      setToast({ open: true, message: error.response?.data?.message || 'Something went wrong', severity: 'error' });
    } finally {
      setProcessing(false);
    }
  };



  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!org) {
    return (
      <PageWrapper>
        <Typography>Organization not found</Typography>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <HeaderCard elevation={0}>
        <IconWrapper>
          <img src={TotalOrgIcon} alt="Org Icon" style={{ width: '32px', height: '32px', filter: 'grayscale(100%)' }} />
        </IconWrapper>
        <HeaderInfo>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
            {org.org_name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            <Typography variant="body2" sx={{ color: '#666', mr: 1 }}>
              ID: {org.org_id}
            </Typography>
            <StatusBadge status={org.status}>
              {org.status}
            </StatusBadge>
          </Box>
        </HeaderInfo>
        
        {org.status !== 'pending' && (
          <HeaderActions>
            {org.status === 'approved' ? (
              <SuspendButton 
                variant="outlined" 
                disabled={processing}
                onClick={() => {
                  setRejectReason('');
                  setOpenRejectDialog(true);
                }}
              >
                {processing ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
                Suspend
              </SuspendButton>
            ) : org.status === 'suspended' ? (
              <ReactivateButton 
                variant="contained" 
                disabled={processing}
                onClick={() => handleUpdateStatus('approved')}
              >
                {processing ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
                Reactivate
              </ReactivateButton>
            ) : null}
          </HeaderActions>
        )}
      </HeaderCard>

      <SectionCard elevation={0}>
        <SectionInfo>
          <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '15px' }}>
            Basic Profile
          </Typography>
          <Typography variant="caption" sx={{ color: '#999' }}>
            The organization's core information and compliance documents
          </Typography>
        </SectionInfo>
        <ReviewLink onClick={() => navigate(`/super-admin/organizations/${id}/profile`)}>
          <Typography variant="body2" sx={{ mr: 1, fontWeight: 500 }}>
            Review the details
          </Typography>
          <ArrowForwardIosIcon sx={{ fontSize: '14px' }} />
        </ReviewLink>
      </SectionCard>

      <SectionCard elevation={0}>
        <SectionInfo>
          <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '15px' }}>
            Organization Staff List
          </Typography>
          <Typography variant="caption" sx={{ color: '#999' }}>
            Manage and review the administrative staff members
          </Typography>
        </SectionInfo>
        <ReviewLink onClick={() => navigate(`/super-admin/organizations/${id}/staff`)}>
          <Typography variant="body2" sx={{ mr: 1, fontWeight: 500 }}>
            Review the details
          </Typography>
          <ArrowForwardIosIcon sx={{ fontSize: '14px' }} />
        </ReviewLink>
      </SectionCard>

      <SectionCard elevation={0}>
        <SectionInfo>
          <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '15px' }}>
            Subscription Plan
          </Typography>
          <Typography variant="caption" sx={{ color: org?.plan_details ? '#999' : '#d32f2f', fontWeight: org?.plan_details ? 400 : 600 }}>
            {org?.plan_details 
              ? `${org.plan_details.plan_name} (Expires: ${org.plan_details.end_date ? dayjs(org.plan_details.end_date).format('DD-MM-YYYY') : 'Never'})`
              : 'No active subscription plan found. Please assign one.'
            }
          </Typography>
        </SectionInfo>
        <ReviewLink onClick={() => navigate(`/super-admin/organizations/${id}/subscription`)}>
          <Typography variant="body2" sx={{ mr: 1, fontWeight: 500 }}>
            {org?.plan_details ? 'Review the details' : 'Assign a Plan'}
          </Typography>
          <ArrowForwardIosIcon sx={{ fontSize: '14px' }} />
        </ReviewLink>
      </SectionCard>

      <VerificationActionCard elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: '40px', color: '#39C471', mr: 2 }} />
          <SectionInfo>
            <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '15px' }}>
              Verification Action
            </Typography>
            <Typography variant="caption" sx={{ color: '#999' }}>
              {org.status === 'pending' 
                ? "Confirm the organization's eligibility and finalize their status"
                : `Current organization status is ${org.status}`
              }
            </Typography>
          </SectionInfo>
        </Box>
        <ActionButtons>
          {org.status === 'pending' ? (
            <>
              <RejectButton 
                variant="outlined" 
                disabled={processing}
                onClick={() => setOpenRejectDialog(true)}
              >
                Reject
              </RejectButton>
              <ApproveButton 
                variant="contained" 
                disabled={processing || !org.is_verified}
                onClick={() => handleUpdateStatus('approved')}
              >
                {processing ? <CircularProgress size={20} color="inherit" /> : 'Approve'}
              </ApproveButton>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic', alignSelf: 'center' }}>
              Verification completed
            </Typography>
          )}
        </ActionButtons>
      </VerificationActionCard>

      {/* Rejection/Suspension Dialog */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {org.status === 'pending' ? 'Reject Organization' : 'Suspend Organization'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
            Please provide a reason for this action. This will be sent to the organization via email.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Reason/Remarks"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <MuiButton onClick={() => setOpenRejectDialog(false)} sx={{ color: '#666' }}>
            Cancel
          </MuiButton>
          <MuiButton 
            onClick={() => handleUpdateStatus(org.status === 'pending' ? 'rejected' : 'suspended', rejectReason)}
            variant="contained"
            color="error"
            disabled={processing || !rejectReason.trim()}
          >
            Confirm {org.status === 'pending' ? 'Reject' : 'Suspend'}
          </MuiButton>
        </DialogActions>
      </Dialog>

      <AlertPopup
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
      />
    </PageWrapper>
  );
};

export default OrganizationOverviewPage;
